import React from 'react';
import { useSelector } from 'react-redux';

import { useGetBarDepositOverviewQuery } from '../../redux/api/barDeliveryApi';

const gebinde = (value) => {
  const n = Number(value);
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
};

const euro = (value) =>
  `${Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/**
 * Operator view of the Pfand ledger, per bar and Pfandgruppe. Empties returns
 * book no stock movement, so this report is the only place the returned crates
 * are visible at all.
 *
 * A negative balance is expected rather than exceptional: crates travel between
 * bars on mixed pallets, so a bar can hand back more than it received. Those
 * rows are flagged so they get reconciled at settlement instead of quietly
 * crediting Pfand nobody was charged.
 */
/**
 * Flaschen/Becher handed out minus returned, one block per item label.
 *
 * Per label rather than one total: adding cups to bottles gives a number nobody
 * can act on. Groups that leave items_per_unit empty contribute nothing here.
 */
const ItemBalances = ({ balances, className = '', heading = false }) => {
  if (!balances || balances.length === 0) return null;
  return (
    <div className={className}>
      {heading && <h6 className='mt-3'>Einzelgebinde</h6>}
      <div className='row'>
        {balances.map((entry) => (
          <div className='col-12 col-md-4' key={entry.item_label}>
            <table className='table table-sm mb-2'>
              <tbody>
                <tr>
                  <td>{entry.item_label} AUSGEGEBEN</td>
                  <td className='text-end'>{entry.handed_out}</td>
                </tr>
                <tr>
                  <td>{entry.item_label} ZURÜCKGEGEBEN</td>
                  <td className='text-end'>{entry.returned}</td>
                </tr>
                <tr className='table-secondary'>
                  <th>{entry.item_label} über/unter</th>
                  <th className='text-end'>{entry.balance}</th>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarDepositOverview = () => {
  const eventId = useSelector((state) => state.auth.currentEvent?.id);
  const { data, isLoading } = useGetBarDepositOverviewQuery(
    { eventId },
    { skip: !eventId }
  );

  if (!eventId) {
    return (
      <div className='alert alert-warning'>Bitte zuerst ein Event wählen.</div>
    );
  }
  if (isLoading) return <p className='text-muted'>Lade Pfandübersicht...</p>;
  if (!data) {
    return (
      <div className='alert alert-danger'>
        Fehler beim Laden der Pfandübersicht.
      </div>
    );
  }

  return (
    <>
      <h4>Pfandübersicht — {data.event_name}</h4>

      <div className='row mt-3'>
        <div className='col-6 col-md-3'>
          <div className='card text-center'>
            <div className='card-body'>
              <div className='text-muted small'>Leergut zurück (netto)</div>
              <div className='fs-4'>{euro(data.totals.returned_value)}</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-3'>
          <div className='card text-center'>
            <div className='card-body'>
              <div className='text-muted small'>noch offen (netto)</div>
              <div className='fs-4 text-danger'>
                {euro(data.totals.outstanding_value)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ItemBalances balances={data.item_balances} className='mt-4' heading />

      {data.locations.length === 0 ? (
        <div className='alert alert-warning mt-4'>
          Keine pfandpflichtigen Bar-Lieferungen für dieses Event.
        </div>
      ) : (
        data.locations.map((location) => (
          <div className='card mt-4' key={location.location_id}>
            <div className='card-body'>
              <h6 className='card-title d-flex justify-content-between'>
                <span>
                  {location.location_name}
                  <span className='text-muted ms-2'>
                    {location.partner_name}
                  </span>
                </span>
                <span className='text-danger'>
                  {location.over_returned && (
                    <span className='badge bg-warning text-dark me-2'>
                      Überhang
                    </span>
                  )}
                  offen: {euro(location.outstanding_value)}
                </span>
              </h6>
              <table className='table table-sm table-striped mb-0'>
                <thead>
                  <tr>
                    <th>Pfandgruppe / Rücknahmeart</th>
                    <th className='text-end'>geliefert</th>
                    <th className='text-end'>Ware retour</th>
                    <th className='text-end'>Menge zurück</th>
                    <th className='text-end'>= Gebinde</th>
                    <th className='text-end'>offen</th>
                    <th className='text-end'>Satz netto</th>
                    <th className='text-end'>gutgeschr. netto</th>
                    <th className='text-end'>offener Wert netto</th>
                    <th className='text-end'>ausgegeben</th>
                    <th className='text-end'>zurück</th>
                  </tr>
                </thead>
                <tbody>
                  {location.deposit_groups.map((row) => (
                    <React.Fragment key={row.deposit_group_id}>
                      <tr>
                        <td>
                          <b>{row.deposit_group_name}</b>
                          {row.over_returned && (
                            <span
                              className='badge bg-warning text-dark ms-2'
                              title='Es kam mehr Leergut zurück als an diese Bar geliefert wurde — bei gemischten Paletten normal, bitte bei der Abrechnung prüfen.'
                            >
                              Überhang
                            </span>
                          )}
                        </td>
                        <td className='text-end'>{row.delivered}</td>
                        <td className='text-end'>{row.goods_returned}</td>
                        <td className='text-end'>&nbsp;</td>
                        <td className='text-end'>
                          {gebinde(row.returned_gebinde)}
                        </td>
                        <td
                          className={`text-end ${
                            Number(row.outstanding) > 0 ? 'text-danger' : ''
                          }${row.over_returned ? ' text-warning-emphasis' : ''}`}
                        >
                          <b>{gebinde(row.outstanding)}</b>
                        </td>
                        <td className='text-end'>
                          {euro(row.deposit_per_unit)}
                        </td>
                        <td className='text-end'>{euro(row.returned_value)}</td>
                        <td className='text-end'>
                          {euro(row.outstanding_value)}
                        </td>
                        <td className='text-end'>
                          {row.counts_items ? row.items_handed_out : '—'}
                        </td>
                        <td className='text-end'>
                          {row.counts_items ? row.items_returned : '—'}
                        </td>
                      </tr>
                      {/* One line per way the Gebinde came back — a bare crate
                          and a single cup are different positions, not one
                          'davon' column that only makes sense for crates. */}
                      {row.returns.map((kind) => (
                        <tr key={kind.deposit_kind_id} className='small'>
                          <td className='ps-4 text-muted'>
                            {kind.deposit_kind_name}
                            {kind.units_per_return > 1 && (
                              <span className='ms-2'>
                                ({kind.units_per_return} = 1 Gebinde)
                              </span>
                            )}
                          </td>
                          <td className='text-end'>&nbsp;</td>
                          <td className='text-end'>&nbsp;</td>
                          <td className='text-end'>{kind.quantity}</td>
                          <td className='text-end'>
                            {gebinde(kind.gebinde_equivalent)}
                          </td>
                          <td className='text-end'>&nbsp;</td>
                          <td className='text-end'>
                            {euro(kind.deposit_per_unit)}
                          </td>
                          <td className='text-end'>{euro(kind.credited)}</td>
                          <td className='text-end'>&nbsp;</td>
                          <td className='text-end'>&nbsp;</td>
                          <td className='text-end'>
                            {row.counts_items ? kind.items_returned : ''}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <ItemBalances balances={location.item_balances} />
            </div>
          </div>
        ))
      )}
    </>
  );
};

export default BarDepositOverview;
